import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import { ConfirmationDialog, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import { useLocation } from 'react-router-dom';
import UserHelper from '../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import { CustomTable } from '../../../components/table/CustomTableWrapper';
import TumCredentialsHttpService from '../../../services/http/masterData/tumCredentialsHttpService';
import { dataSetOrganizations, dataSetTumCredentialsList } from '../../../store/masterData/Actions';
import TumCredentialsModelHelper from '../../../@types/model/masterData/tumCredentials/tumCredentialsModelHelper';
import { ITumCredentialsFormValues } from '../../../@types/model/masterData/tumCredentials/tumCredentialsFormValues';
import { ITumCredentials } from '../../../@types/model/masterData/tumCredentials/tumCredentials';
import TumCredentialsEditForm from './form/TumCredentialsEditForm';
import OrganizationHttpService from '../../../services/http/masterData/organizationHttpService';

const TumCredentialsScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const tumCredentialsList = useAppSelector(x => x.masterData.tumCredentialsList);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedTumCredentials, setSelectedTumCredentials] = useState<ITumCredentials | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deletingTumCredentials, setDeletingTumCredentials] = useState<ITumCredentials | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const res = await TumCredentialsHttpService.getList([]);
            const orgRes = await OrganizationHttpService.getOrganizationData();
            dispatch(dataSetTumCredentialsList(res.data));
            dispatch(dataSetOrganizations(orgRes.data));
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading tum credentials data.');
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (values : ITumCredentialsFormValues) => {
        setIsLoading(true);

        try {
            const upsert = TumCredentialsModelHelper.createUpsert(values);
            const res = await TumCredentialsHttpService.upsert(upsert);
            const updatedList = upsertArrayElement(tumCredentialsList, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetTumCredentialsList(updatedList));

            generalShowSuccessSnackbar(selectedTumCredentials ? 'Tum credentials updated successfully.' : 'Tum credentials added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating tum credentials data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeTumCredentials = async () => {
        if (!deletingTumCredentials) return;
        const updatedTumCredentials = { ...deletingTumCredentials, isActive: false };
        setIsLoading(true);
        try {
            const res = await TumCredentialsHttpService.delete(updatedTumCredentials.id);
            const updatedList = upsertArrayElement(tumCredentialsList, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetTumCredentialsList(updatedList));
            generalShowSuccessSnackbar('Tum credentials deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting tum credentials.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedTumCredentials(null);
    };

    const edit = (selectedRow : ITumCredentials) => {
        setSelectedTumCredentials(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : ITumCredentials) : void => {
        setDeletingTumCredentials(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingTumCredentials(null);
    };

    const deleteButtonTooltip = (row : ITumCredentials) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Tum Credentials', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return TumCredentialsModelHelper.createFormValues(selectedTumCredentials);
        }
    }, [selectedTumCredentials, isDialogOpen]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
    *                                                  Render Methods
    * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className={'fdc hfill'}>
                <Card className={'fdc hfill'}>
                    <CustomTable<ITumCredentials>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Tum Credentials', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Tum Credentials', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : ITumCredentials) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Client ID', field: 'clientId', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={tumCredentialsList}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedTumCredentials ? 'Edit Tum Credentials' : 'Add Tum Credentials'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <TumCredentialsEditForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Tum Credentials'}
                open={!!deletingTumCredentials}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeTumCredentials}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default TumCredentialsScreen;
